import type { AxiosInstance, AxiosRequestConfig } from "axios";
import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type * as Paths from "./paths";

interface OperationMethods {
  "createOrder"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateOrder.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateOrder.Responses.$200>;
  "processOrder"(
    parameters?: Parameters<Paths.ProcessOrder.PathParameters> | null,
    data?: Paths.ProcessOrder.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.ProcessOrder.Responses.$200>;

  "cancelOrder"(
    parameters?: Parameters<Paths.CancelOrder.PathParameters> | null,
    data?: Paths.CancelOrder.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CancelOrder.Responses.$200>;

  "getOrderBasketSearchItems"(
    parameters?: Parameters<Paths.GetOrderBasketSearchItems.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetOrderBasketSearchItems.Responses.$200>;

  "getOrderBasketItems"(
    parameters?: Parameters<Paths.GetOrderBasketItems.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetOrderBasketItems.Responses.$200>;

  "updateOrderBasketData"(
    data?: Paths.updateOrderBasketData.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.updateOrderBasketData.Responses.$200>;

  "getOrderBasketSearchCustomers"(
    parameters?: Parameters<Paths.getOrderBasketSearchCustomers.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.getOrderBasketSearchCustomers.Responses.$200>;
  "updateOrderBasketVoucherData"(
    data?: Paths.UpdateOrderBasketVoucherData.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateOrderBasketVoucherData.Responses.$200>;
  "deleteOrderBasketData"(
    parameters?: UnknownParamsObject,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<any>;
  "takeOverCustomer"(
    data?: Paths.TakeOverCustomer.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.TakeOverCustomer.Responses.$200>;
  "updateAddress"(
    parameters: Parameters<Paths.UpdateAddress.PathParameters> | null,
    data: Paths.UpdateAddress.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateAddress.Responses.$200>;
}

export const getOrderOperations = (client: AxiosInstance) => {
  const createOrder: OperationMethods["createOrder"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/basket/create-order";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const processOrder: OperationMethods["processOrder"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/order/{order}/process";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const cancelOrder: OperationMethods["cancelOrder"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/order/{order}/cancel";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const getOrderBasketSearchItems: OperationMethods["getOrderBasketSearchItems"] =
    (parameters, data, config) => {
      const url = "/basket/search/items";
      return client.get(replacePathParameters(url, parameters), {
        params: omitPathParameters(parameters, url),
        data: data,
        ...config,
      });
    };

  const getOrderBasketItems: OperationMethods["getOrderBasketItems"] = (
    data,
    config,
  ) => {
    const url = "/basket";
    return client.get(url, {
      data: data,
      ...config,
    });
  };

  const updateOrderBasketData: OperationMethods["updateOrderBasketData"] = (
    data,
    config,
  ) => {
    const url = "/basket";
    return client.put(url, data, {
      ...config,
    });
  };

  const getOrderBasketSearchCustomers: OperationMethods["getOrderBasketSearchCustomers"] =
    (parameters, data, config) => {
      const url = "/basket/search/customers";
      return client.get(replacePathParameters(url, parameters), {
        params: omitPathParameters(parameters, url),
        data: data,
        ...config,
      });
    };

  const updateOrderBasketVoucherData: OperationMethods["updateOrderBasketVoucherData"] =
    (data, config) => {
      const url = "/basket";
      return client.put(url, data, {
        ...config,
      });
    };

  const deleteOrderBasketData: OperationMethods["deleteOrderBasketData"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/basket";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  const takeOverCustomer: OperationMethods["takeOverCustomer"] = (
    data,
    config,
  ) => {
    const url = "/basket/take-over-customer";
    return client.put(url, data, { ...config });
  };

  const updateAddress: OperationMethods["updateAddress"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/order/{id}/address";
    return client.patch(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  return {
    createOrder,
    processOrder: processOrder,
    cancelOrder: cancelOrder,
    getOrderBasketSearchItems: getOrderBasketSearchItems,
    getOrderBasketItems: getOrderBasketItems,
    updateOrderBasketData: updateOrderBasketData,
    getOrderBasketSearchCustomers: getOrderBasketSearchCustomers,
    updateOrderBasketVoucherData: updateOrderBasketVoucherData,
    deleteOrderBasketData: deleteOrderBasketData,
    takeOverCustomer: takeOverCustomer,
    updateAddress,
  };
};
